/**
 * Footer Component
 * 
 * Main application footer with links and copyright information.
 */
import { Link } from 'react-router-dom'
import { cn, twStyles } from '../../utils/tailwind-utils'

interface FooterProps {
  className?: string
}

function Footer({ className }: FooterProps) {
  const currentYear = new Date().getFullYear()
  
  return (
    <footer className={cn('bg-card border-t border-border py-8', className)}>
      <div className={cn(twStyles.container.base, 'grid grid-cols-1 md:grid-cols-4 gap-8')}>
        {/* Brand and Description */}
        <div className="md:col-span-1">
          <h2 className="text-xl font-bold text-primary mb-4">Boardgames Today</h2>
          <p className="text-muted-foreground">
            Discover, schedule, and join board game events in your area.
            Connect with fellow board game enthusiasts!
          </p>
        </div>
        
        {/* Quick Links */}
        <div>
          <h3 className="font-semibold mb-4">Quick Links</h3>
          <ul className="space-y-2">
            <li><FooterLink to="/">Home</FooterLink></li>
            <li><FooterLink to="/events">Events</FooterLink></li>
            <li><FooterLink to="/games">Games</FooterLink></li>
            <li><FooterLink to="/about">About</FooterLink></li>
          </ul>
        </div>
        
        {/* Resources */}
        <div>
          <h3 className="font-semibold mb-4">Resources</h3>
          <ul className="space-y-2">
            <li><FooterLink to="/faq">FAQ</FooterLink></li>
            <li><FooterLink to="/support">Support</FooterLink></li>
            <li><FooterLink to="/blog">Blog</FooterLink></li>
          </ul>
        </div>
        
        {/* Legal */}
        <div>
          <h3 className="font-semibold mb-4">Legal</h3>
          <ul className="space-y-2">
            <li><FooterLink to="/terms">Terms of Service</FooterLink></li>
            <li><FooterLink to="/privacy">Privacy Policy</FooterLink></li>
            <li><FooterLink to="/cookies">Cookie Policy</FooterLink></li>
          </ul>
        </div>
      </div>
      
      {/* Bottom Bar */}
      <div className={cn(twStyles.container.base, 'mt-8 pt-6 border-t border-border')}>
        <div className="flex flex-col md:flex-row justify-between items-center">
          <p className="text-sm text-muted-foreground">
            &copy; {currentYear} Boardgames Today. All rights reserved.
          </p>
          <div className="flex space-x-4 mt-4 md:mt-0">
            <SocialLink href="https://twitter.com" aria-label="Twitter">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
                <path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z"></path>
              </svg>
            </SocialLink>
            <SocialLink href="https://facebook.com" aria-label="Facebook">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
                <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
              </svg>
            </SocialLink>
            <SocialLink href="https://instagram.com" aria-label="Instagram">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
              </svg>
            </SocialLink>
          </div>
        </div>
      </div>
    </footer>
  )
}

interface FooterLinkProps {
  to: string
  children: React.ReactNode
}

function FooterLink({ to, children }: FooterLinkProps) {
  return (
    <Link 
      to={to} 
      className="text-muted-foreground hover:text-foreground transition-colors"
    >
      {children}
    </Link>
  )
}

interface SocialLinkProps {
  href: string
  children: React.ReactNode
  'aria-label': string
}

function SocialLink({ href, children, ...props }: SocialLinkProps) {
  return (
    <a 
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-muted-foreground hover:text-foreground transition-colors"
      {...props}
    >
      {children}
    </a>
  )
}

export default Footer
